import * as React from 'react';

import './Browser.css';

import { Card, ICardProps } from '../Elements/Card'
import PlaceholderToolCard from '../Elements/PlaceholderToolCard'
import IDragStartChainable from '../IDragStartChainable'
import { IDropZone, IDropZoneProps } from '../IDropZone'

import ToolCard from '../Elements/ToolCard'

import { ToolsManager } from '../ToolsManager';
import Search from './Search'

import * as _ from 'underscore'
import { ToolId } from '../../Api/Tools';

interface IBrowserProps extends IDropZoneProps<Browser>, IDragStartChainable {
    onRef: (browserInstance: Browser | null) => void;
}

interface IBrowserState {
    search: Search;
    
    contentSetupRunning: boolean;
    viewFillOrdered: boolean;
    viewExtensionOrdered: boolean;
    placeholders: number;

    totalCount: number;
    tools: ToolId[];
}

type OnDoneCallback = () => void;

class Browser extends React.Component<IBrowserProps, IBrowserState> implements IDropZone<Card<ICardProps, any>> {
    /* 
        These are instance-specific fields that grant us access to HTML elements and th-
        eir attributes/events/etc.
    */
    private browserRef: any;
    private scrollableRef: any;
    private gridRef: any;

    /*
        Underscore-made throttled functions to throttle some of the events.
    */
    private resizeThrottled: () => void;
    private scrollThrottled: () => void;
    
    /*
        These fields are initialized the first time tools are being loaded by distribut-
        ing placeholders, for more information see the documentation for setupContent.

        These defaults are later used when the content currently loaded in the content-
        grid does not provide the information we need (e. g. search with too few result-
        s to break a line or too few results to fill the screen to figure out the numbe-
        r of lines).
    */
    private defaultColumnCount: number;
    private defaultRowCount: number;

    /*
        Initializes all fields to sensible defaults
    */
    public constructor(props: IBrowserProps) {
        super(props);
        
        this.browserRef = React.createRef();
        this.scrollableRef = React.createRef();
        this.gridRef = React.createRef();

        this.resizeThrottled = _.debounce(this.resizeTriggered, 350);
        this.scrollThrottled = _.throttle(this.scrolled, 250);
        
        this.defaultColumnCount = 0;
        this.defaultRowCount = 0;

        this.state = {
            search: new Search(""),

            contentSetupRunning: false,
            viewFillOrdered: false,
            viewExtensionOrdered: false,
            placeholders: 0,

            totalCount: 0,
            tools: []
        };
    }

    /*
        Basic component lifecycle method
        - Mounts to the parent component
        - Sets up global event handling
        - Calculates the content defaults using placeholders
        - Orders to load first tools to view
    */
    public componentDidMount() {
        this.props.onRef(this);

        if (window.addEventListener) {
            window.addEventListener("resize", this.resizeThrottled, false);
        } else if ((window as any).attachEvent) {
            (window as any).attachEvent("onresize", this.resizeThrottled);
        }

        /* the defaults for setupContent to use further */
        this.setupContent(() => {

            this.setState({
                contentSetupRunning: false
            });
        });
    }

    public componentWillUnmount() {
        this.props.onRef(null);
    }
  
    /*
        Basic lifecycle render
    */
    public render() {
        return (
            <div className="Browser" onScroll={this.scrollThrottled} ref={this.browserRef} onMouseEnter={this.mouseEntered} onMouseLeave={this.mouseLeft}>
                <div className="Browser__info-bar">
                    <b>{this.state.tools.length}<b /></b> of {this.state.totalCount} <span className="opaqueText">Tools<b /></span>
                </div>

                <div className={"Browser__scrollable" + (!this.props.dragActive ? " Browser__scrollable--no-drag" : "")} ref={this.scrollableRef}>
                    <div
                        className={"Browser__content-grid" + (this.props.dragActive ? " Browser__content-grid--drag" : "")}
                        ref={this.gridRef}
                        >
                        {this.renderGrid()}
                    </div>
                </div>
            </div>
        );
    }

    public shouldComponentUpdate(nextProps: IBrowserProps, nextState: IBrowserState, nextContext: any) : boolean {

        if(JSON.stringify(this.props) !== JSON.stringify(nextProps)) {
            /* situation: the props supplied differ */

            /* a quick shallow comparison of the props, quite future-proof */
            // console.log("Browser.shouldComponentUpdate(): props differ", JSON.stringify(this.props), JSON.stringify(nextProps));

            /* verdict: do update */
            return true;
        }

        /* 
            - if any of the orders can lead to visual adjustment, plan out the adjustment in the state and return false;
            - if not, return true if any of the other fields of the state has changed
        */
        if(this.state.search.getTimestamp() !== nextState.search.getTimestamp()) {
            /* situation: the search "command" has been issued (simply put, SearchBox has decided that a new search should happen) */

            this.scrollableRef.current.scrollTop = 0;

            const toolsWantedCount = this.getCurrentColumnCount() * this.getCurrentRowCount();

            this.setState({
                viewFillOrdered: true,
                viewExtensionOrdered: false,
                placeholders: toolsWantedCount,

                totalCount: 0,
                tools: []
            });

            /* verdict: do not update as there is no change in the visual state, but note the change in placeholders/totalCount/tools above will trigger an update */
            return false;
        }

        if(nextState.viewFillOrdered) {
            if(!this.hasReachedEnd()) {
                if(!this.isScrollBarVisible()) {
                    if(nextState.placeholders === 0) {
                        /* situation: we can load more tools, there is no scroll bar and no placeholders for further loading are active */

                        this.setState({
                            placeholders: this.calculateToolsToLoad(1)
                        });
                        
                        /* verdict: the change in placeholders will trigger update, return false as we do not need any more changes now */
                        return false;
                    } else {
                        /* situation: we can load more tools, there is no scroll bar but placeholders for further loading are active */

                        /* verdict: pass program flow further to detect other changes that might call for a re-render */
                    }
                } else {
                    if(nextState.placeholders === 0) {
                         /* situation: we can load more tools, but the scrollbar is already visible and no placeholders for further loading are active */
                        this.setState({
                            viewFillOrdered: false
                        });

                        this.defaultColumnCount = this.getCurrentColumnCount();
                        this.defaultRowCount = this.getCurrentRowCount();

                        /* verdict: pass program flow further to detect changes that might call for a re-render */
                    } else {
                        /* situation: we can load more tools, but the scrollbar is already visible and placeholders for further loading are active */
                        
                        /* verdict: pass program flow further to detect changes that might call for a re-render */
                    }
                }
            } else {
                /* situation: we can not load more tools */

                this.setState({
                    viewFillOrdered: false,
                    placeholders: 0
                });

                this.defaultColumnCount = this.getCurrentColumnCount();

                /* verdict: program flow passed further - if there are any other changes we follow, it will re-render */
            }
        }

        if(nextState.viewExtensionOrdered) {
            if(!this.hasReachedEnd()) {
                if(this.hasReachedBottom()) {
                    if(nextState.placeholders === 0) {
                        /* situation: we can load more tools, the user has reached the bottom of the currently loaded directory and no placeholders for further loading are active */
                        
                        this.setState({
                            placeholders: this.calculateToolsToLoad(3)
                        });
                        
                        /* verdict: the change in placeholders will trigger update, return false as we do not need any more changes now */
                        return false;
                    } else {
                        /* situation: we can load more tools, the user has reached the bottom of the currently loaded directory and but placeholders for further loading are active */
                        
                        /* verdict: pass program flow further to detect changes that might call for a re-render */
                    }
                } else {
                    if(nextState.placeholders === 0) {
                         /* situation: we can load more tools, but we are not at the bottom anymore and no placeholders for further loading are active */
                        this.setState({
                            viewExtensionOrdered: false
                        });

                        /* verdict: pass program flow further to detect changes that might call for a re-render */
                    } else {
                        /* situation: we can load more tools, but we are not at the bottom anymore and placeholders for further loading are active */
                        
                        /* verdict: pass program flow further to detect changes that might call for a re-render */
                    }
                }
            } else {
                if(nextState.placeholders === 0) {
                    /* situation: we can not load more tools and no placeholders for further loading are active */

                    this.setState({
                        viewExtensionOrdered: false
                    });

                    /* verdict: program flow passed further - if there are any other changes we follow, it will re-render */
                } else {
                    /* situation: we can not load more tools */

                    this.setState({
                        viewExtensionOrdered: false,
                        placeholders: 0
                    });

                    /* verdict: program flow passed further - if there are any other changes we follow, it will re-render */
                }
            }
        }

        
        if(this.state.contentSetupRunning) {
            /* situation: we are in the middle of a content setup */

            /* verdict: always update if we're just setting up the content */
            return true;
        }

        /* a shallow comparison for the rest of the state variables */
        if(this.state.placeholders !== nextState.placeholders) {
            return true;
        }

        if(this.state.tools !== nextState.tools) {
            return true;
        }

        if(this.state.totalCount !== nextState.totalCount) {
            return true;
        }

        /* situation: no state changes that would require an update have changed */
        /* verdict: do not update */
        return false;
    }

    public componentDidUpdate(prevProps: IBrowserProps, prevState: IBrowserState) {
        
        if(this.state.contentSetupRunning) {
            /* situation: the content setup is running */
            /* verdict: prevent tool loading from happening, we literally want just the placeholders */
            return;
        }

        /* situation: there are placeholders calling for some content to be loaded */
        if(this.state.placeholders > 0) {
            this.loadTools(this.state.placeholders);
        }
    }

    /*
        Takes care of the search-phrase changes
    */
    public searchTriggered(search: string) : void {
        this.setState({
            search: new Search(search),
        });
    }

    public resizeTriggered = () : void => {
        this.setState({
            viewFillOrdered: true
        });
    };

    public handleDrop(object: Card<ICardProps, any>) {
        /* do nothin' jon snow */
        // currently used for discarding cards
    }

    private renderGrid() : any[] {
        const buffer = []

        for(const tool of ToolsManager.getTools(this.state.tools)) {
            buffer.push(
            <ToolCard
                name={tool.name}
                toolId={tool.id}

                time={tool.timeRange}
                participants={tool.participantsRange}

                shortDescription={tool.shortDescription}
                source={tool.sources[0]}
                onDragStart={this.props.onDragStart}

                key={tool.id}
                />);
        }

        for(let i = 0; i < this.state.placeholders;++i) {
            buffer.push(
                <PlaceholderToolCard
                    name="PLACEHOLD PLACE"
                    
                    toolId="invalid_key"
                    time="30-60"
                    participants="2+"

                    key={"placeholder_"+i}
                    />);
        }

        return buffer;
    }

    /*
        The handler for scroll events
        - attempts to load more tools if:
            - the user has scrolled down almost to the bottom of the currently loaded content
            - the loading hasn't reached the bottom
    */
    private scrolled = () => {
        this.setState({
            viewExtensionOrdered: true
        });
    }

    /*
        Finds out whether the vertical scrollbar is visible or not
    */
    private isScrollBarVisible() : boolean {
        return this.scrollableRef.current.scrollHeight > this.scrollableRef.current.clientHeight;
    }

    /*
        Figures out the basic dimensions of the content (ToolCard) grid
    */
    private setupContent(onDone?: OnDoneCallback) {
        this.defaultRowCount = 1;
        this.defaultColumnCount = 0;

        this.setupColumns(() => { 
            this.setupRows(onDone);
        });
    }

    /*
        In a chain of recursive calls to itself finds the number of tools that need to be loaded to fill a single row of the content (ToolCard) grid
    */
    private setupColumns(onDone?: OnDoneCallback) {
        ++this.defaultColumnCount;

        this.setState({
            contentSetupRunning: true,
            placeholders: this.state.placeholders + 1
        }, () => {
            const gridElement = this.gridRef.current;
            const toolCardNodes = gridElement.childNodes;

            /* find the number of elements in a row */
            let lastOffset = -1;
            let broke: boolean = false;
            for(const toolCardNode of toolCardNodes) {
                const newOffset = toolCardNode.getBoundingClientRect().left;
                if(newOffset > lastOffset) {
                    lastOffset = newOffset;
                } else {
                    broke = true;
                    break;
                }
            }

            if(broke) {
                --this.defaultColumnCount;
                this.setState({
                    contentSetupRunning: true,
                    placeholders: this.state.placeholders - 1
                }, onDone);
            } else {
                this.setupColumns(onDone);
            }
        });
    }

    /*
        In a chain of recursive calls to itself finds the number of rows that need to be loaded to fill users view - the whole of the content (ToolCard) grid
    */
    private setupRows(onDone?: OnDoneCallback) {
        ++this.defaultRowCount;

        this.setState({
            contentSetupRunning: true,
            placeholders: this.defaultColumnCount * this.defaultRowCount
        }, () => {
            if(this.isScrollBarVisible()) {
                if(onDone) {
                    onDone();
                }
            } else {
                this.setupRows(onDone);
            }
        });
    }

    private loadTools(count: number, onLoad?: (toolsWantedCount: number, toolsLoadedCount: number) => void) {
        if(this.state.search.isLoading()) {
            return;
        }

        this.state.search.setIsLoading(true);

        ToolsManager.loadToolsNext(
            this.state.search.getTimestamp(),
            this.state.tools.length,
            this.state.search.getPhrase(),
            count,
            (requestTime: number, totalCountFromServer: number, tools: ToolId[]) => {

                /* order is important here */
                if(requestTime < this.state.search.getTimestamp()) {
                    return;
                }

                this.state.search.setIsLoading(false);

                this.setState({
                    totalCount: totalCountFromServer,

                    placeholders: (this.hasReachedEnd() ? 0 : this.state.placeholders - tools.length),
                    tools: this.state.tools.concat(tools),
                }, () => {
                    if(onLoad) {
                        onLoad(count, tools.length);
                    }
                });
            }, () => {
                this.state.search.setIsLoading(false);
            }
        );
    }

    /*
       Find out the number of tools to load to get `rowsDesired` additional row

        rowsDesired:
            - 0 - for row completion onlye
            - n - (where n>0) for row completion and loading of the n following rows.
                    The completed row therefore does not count to n!
     */
    private calculateToolsToLoad(rowsDesired: number) : number {
        const columnCount = this.getCurrentColumnCount();

        /* find how many elements to load:
            1. calculate the number of elements to load to complete the last row
        */
        let elementsToLoad = columnCount - (this.state.tools.length % columnCount);
        if(rowsDesired > 0) {
            if(elementsToLoad === columnCount) {
                --rowsDesired;
            }

            elementsToLoad += columnCount * rowsDesired;
        } else {
            if(elementsToLoad === columnCount) {
                elementsToLoad = 0;
            }
        }
        
        return elementsToLoad;
    }

    /*
        Returns the current number of columns, or the default if the current number of columns is too small to fill a row of the content grid
    */
    private getCurrentColumnCount() : number {
        const gridElement = this.gridRef.current;
        const toolCardNodes = gridElement.childNodes;

        /* find the number of elements in a row (elementCount) */
        let lastVal : number = -1;
        let elementCount : number = 0;
        let broke : boolean = false;
        for(const toolCardNode of toolCardNodes) {
            const currentVal = toolCardNode.getBoundingClientRect().left;
            if(currentVal > lastVal) {
                ++elementCount;
                lastVal = currentVal;
            } else {
                broke = true;
                break;
            }
        }
        if(broke) {
            return elementCount;
        } else {
            return this.defaultColumnCount;
        }
    }

    /*
        Returns the current number of rows, or the default if the current number of rows is too small to fill the content grid in the view
    */
    private getCurrentRowCount() : number {
        const gridElement = this.gridRef.current;
        const toolCardNodes = gridElement.childNodes;
        const clientHeight = this.scrollableRef.current.clientHeight;

        /* find the number of elements in a row (elementCount) */
        let rowCount : number = 0;
        let broke : boolean = false;
        let currentOffset : number = -1;
        for(const toolCardNode of toolCardNodes) {
            const newOffset : number = toolCardNode.getBoundingClientRect().top;
            const height : number = toolCardNode.getBoundingClientRect().height;
            if(newOffset > currentOffset) {
                currentOffset = newOffset;
                ++rowCount;

                if(currentOffset+height > clientHeight) {
                    broke = true;
                    break;
                }
            }
        }
        
        if(broke) {
            return rowCount+1; // TODO: verify this fix
        } else {
            return this.defaultRowCount;
        }
    }

    private hasReachedBottom() : boolean {
        const bottomPos = this.scrollableRef.current.scrollTop + this.scrollableRef.current.clientHeight;
        const limitPos = this.scrollableRef.current.scrollHeight;
        if(bottomPos >= limitPos - 24) { /* standard google material design offset used, see CSS, might want to use a CSS variable instead*/
            return true;
        } else {
            return false;
        }
    }

    private hasReachedEnd() : boolean {
        return this.state.tools.length === this.state.totalCount;
    }

    private mouseEntered = () => {
        if(this.props.onDropZoneEnter) {
            this.props.onDropZoneEnter(this);
        }
    };

    private mouseLeft = () => {
        if(this.props.onDropZoneLeave) {
            this.props.onDropZoneLeave(this);
        }
    };
}

export default Browser;


// WEBPACK FOOTER //
// ./src/App/Blocks/Browser.tsx